import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { invoiceImportApi, projectsApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";

import { sv } from "date-fns/locale";
import {
  Upload, FileText, Trash2, Eye, Link2, AlertTriangle,
  CheckCircle2, HelpCircle, TrendingUp, TrendingDown, ArrowUpDown,
  Receipt, Building2, Hash, Calendar, DollarSign, Search, Mail, Loader2,
} from "lucide-react";

const InvoiceImport = () => {
  const { isAdmin, canManage } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [fetchingEmail, setFetchingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterProject, setFilterProject] = useState("all");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [matchProject, setMatchProject] = useState("");
  const [tab, setTab] = useState("invoices"); // invoices | comparison

  const loadData = useCallback(async () => {
    try {
      const [invRes, projRes, emailRes] = await Promise.all([
        invoiceImportApi.getAll(),
        projectsApi.getAll(),
        invoiceImportApi.getEmailStatus().catch(() => ({ data: { configured: false } })),
      ]);
      setInvoices(invRes.data);
      setProjects(projRes.data.filter(p => p.is_active));
      setEmailStatus(emailRes.data);
    } catch {
      toast.error("Kunde inte ladda data");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadComparison = useCallback(async () => {
    try {
      const res = await invoiceImportApi.getComparison();
      setComparison(res.data);
    } catch {
      toast.error("Kunde inte ladda jämförelse");
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (tab === "comparison") loadComparison();
  }, [tab, loadComparison]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    let count = 0;
    for (const file of files) {
      if (file.size > 20 * 1024 * 1024) {
        toast.error(`${file.name} är för stor (max 20MB)`);
        continue;
      }
      try {
        await invoiceImportApi.upload(file);
        count++;
      } catch {
        toast.error(`Kunde inte ladda upp ${file.name}`);
      }
    }
    if (count > 0) {
      toast.success(`${count} ${count === 1 ? "faktura" : "fakturor"} importerad${count > 1 ? "e" : ""}`);
      loadData();
    }
    setUploading(false);
    e.target.value = "";
  };

  const handleFetchEmail = async () => {
    setFetchingEmail(true);
    try {
      const res = await invoiceImportApi.fetchFromEmail();
      const data = res.data;
      if (data.imported > 0) {
        toast.success(`${data.imported} ${data.imported === 1 ? "faktura" : "fakturor"} importerad${data.imported > 1 ? "e" : ""} från e-post`);
        loadData();
      } else {
        toast.info(data.message || "Inga nya fakturor hittades");
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Kunde inte hämta e-post";
      toast.error(detail);
    } finally {
      setFetchingEmail(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await invoiceImportApi.delete(id);
      setInvoices(prev => prev.filter(i => i.id !== id));
      toast.success("Faktura borttagen");
    } catch {
      toast.error("Kunde inte ta bort");
    }
  };

  const handleManualMatch = async (invoiceId, projectId) => {
    try {
      const res = await invoiceImportApi.update(invoiceId, { project_id: projectId });
      setInvoices(prev => prev.map(i => i.id === invoiceId ? res.data : i));
      setDetailOpen(false);
      toast.success("Faktura kopplad till projekt");
    } catch {
      toast.error("Kunde inte koppla faktura");
    }
  };

  const handleViewFile = (inv) => {
    const token = localStorage.getItem("token");
    window.open(`${invoiceImportApi.fileUrl(inv.id)}?token=${token}`, "_blank", "noopener,noreferrer");
  };

  const openDetail = (inv) => {
    setSelectedInvoice(inv);
    setMatchProject(inv.project_id || "");
    setDetailOpen(true);
  };

  const getStatusBadge = (status) => {
    const map = {
      auto_matchad: { label: "Auto-matchad", icon: CheckCircle2, cls: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
      manuellt_matchad: { label: "Manuellt matchad", icon: Link2, cls: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
      ej_matchad: { label: "Ej matchad", icon: HelpCircle, cls: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
    };
    const info = map[status] || map.ej_matchad;
    const Icon = info.icon;
    return (
      <Badge className={`${info.cls} gap-1`} variant="outline">
        <Icon className="w-3 h-3" /> {info.label}
      </Badge>
    );
  };

  const filtered = invoices.filter(inv => {
    if (filterStatus !== "all" && inv.match_status !== filterStatus) return false;
    if (filterProject !== "all" && inv.project_id !== filterProject) return false;
    return true;
  });

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6dbf4a]" /></div>;
  }

  return (
    <div className="space-y-5 slide-up" data-testid="invoice-import-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Receipt className="w-5 h-5 text-[#6dbf4a]" />
            <h1 className="text-2xl font-bold text-white tracking-tight">Fakturaimport</h1>
          </div>
          <p className="text-white/25 text-sm">Importera och matcha leverantörsfakturor</p>
        </div>
      <div className="flex items-center gap-2">
        {emailStatus?.configured && (
          <Button
            onClick={handleFetchEmail}
            disabled={fetchingEmail}
            variant="outline"
            className="border-white/[0.06] text-white/40 hover:bg-[#333] hover:text-white gap-2"
            data-testid="fetch-email-btn"
          >
            {fetchingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            {fetchingEmail ? "Hämtar..." : "Hämta från e-post"}
          </Button>
        )}
        {emailStatus && !emailStatus.configured && (
          <span className="text-xs text-white/40 flex items-center gap-1 mr-2">
            <Mail className="w-3.5 h-3.5" /> E-post ej konfigurerad
          </span>
        )}
        <label className={`flex items-center gap-2 px-4 py-2 rounded-md cursor-pointer transition-colors text-sm font-medium ${
          uploading ? "bg-[#333] text-white/40" : "bg-[#6dbf4a] hover:bg-[#5da83d] text-white"
        }`} data-testid="invoice-upload-btn">
          <Upload className="w-4 h-4" />
          {uploading ? "Laddar..." : "Ladda upp"}
          <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="hidden"
            onChange={handleUpload} disabled={uploading} data-testid="invoice-file-input" />
        </label>
      </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#080c12] rounded-xl p-1 w-fit" data-testid="invoice-tabs">
        <button
          onClick={() => setTab("invoices")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === "invoices" ? "bg-[#6dbf4a] text-white" : "text-white/40 hover:text-white"
          }`} data-testid="tab-invoices">
          <Receipt className="w-4 h-4 inline mr-1.5" />Fakturor ({invoices.length})
        </button>
        <button
          onClick={() => setTab("comparison")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === "comparison" ? "bg-[#6dbf4a] text-white" : "text-white/40 hover:text-white"
          }`} data-testid="tab-comparison">
          <ArrowUpDown className="w-4 h-4 inline mr-1.5" />Jämförelse
        </button>
      </div>

      {/* INVOICES TAB */}
      {tab === "invoices" && (
        <>
          {/* Filters */}
          <div className="flex gap-3 items-center flex-wrap">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="bg-[#0d1218] border-white/[0.06] text-white w-48" data-testid="filter-status">
                <Search className="w-4 h-4 mr-2 text-white/40" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0d1218] border-white/[0.06]">
                <SelectItem value="all" className="text-white">Alla statusar</SelectItem>
                <SelectItem value="auto_matchad" className="text-white">Auto-matchade</SelectItem>
                <SelectItem value="manuellt_matchad" className="text-white">Manuellt matchade</SelectItem>
                <SelectItem value="ej_matchad" className="text-white">Ej matchade</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterProject} onValueChange={setFilterProject}>
              <SelectTrigger className="bg-[#0d1218] border-white/[0.06] text-white w-56" data-testid="filter-project">
                <SelectValue placeholder="Alla projekt" />
              </SelectTrigger>
              <SelectContent className="bg-[#0d1218] border-white/[0.06]">
                <SelectItem value="all" className="text-white">Alla projekt</SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id} className="text-white">{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {invoices.filter(i => i.match_status === "ej_matchad").length > 0 && (
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 px-3 py-1.5">
                <AlertTriangle className="w-3.5 h-3.5 mr-1" />
                {invoices.filter(i => i.match_status === "ej_matchad").length} ej matchade
              </Badge>
            )}
          </div>

          {/* Invoice list */}
          {filtered.length > 0 ? (
            <div className="space-y-2">
              {filtered.map(inv => (
                <div key={inv.id}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#0d1218] hover:bg-[#0d1218] transition-colors group cursor-pointer"
                  onClick={() => openDetail(inv)} data-testid={`invoice-${inv.id}`}>
                  <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-red-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-white font-medium truncate">{inv.filename}</p>
                      {getStatusBadge(inv.match_status)}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-white/40 mt-0.5">
                      {inv.supplier_name && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{inv.supplier_name}</span>}
                      {inv.invoice_number && <span className="flex items-center gap-1"><Hash className="w-3 h-3" />{inv.invoice_number}</span>}
                      {inv.invoice_amount != null && <span className="flex items-center gap-1 text-[#6dbf4a]"><DollarSign className="w-3 h-3" />{inv.invoice_amount.toLocaleString("sv-SE")} kr</span>}
                      {inv.project_name && <span className="flex items-center gap-1 text-blue-400"><Link2 className="w-3 h-3" />{inv.project_name}</span>}
                      {inv.source === "email" && <span className="flex items-center gap-1 text-purple-400"><Mail className="w-3 h-3" />E-post</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-white/40 hover:text-white"
                      onClick={(e) => { e.stopPropagation(); handleViewFile(inv); }}><Eye className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-white/40 hover:text-red-400"
                      onClick={(e) => { e.stopPropagation(); handleDelete(inv.id); }}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Receipt className="w-10 h-10 mx-auto mb-3 text-gray-700" />
              <p className="text-white/40 text-sm">Inga fakturor importerade ännu</p>
              <p className="text-white/25 text-xs mt-1">Ladda upp PDF-fakturor så matchas de automatiskt mot arbetsnummer</p>
            </div>
          )}
        </>
      )}

      {/* COMPARISON TAB */}
      {tab === "comparison" && comparison && (
        <div className="space-y-6">
          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-[#2a2a2a] to-[#222] border-white/[0.06] overflow-hidden relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-red-500" />
              <CardContent className="pt-6 pb-5">
                <p className="text-xs uppercase tracking-wider text-white/40 mb-2">Fakturerat</p>
                <p className="text-3xl font-bold text-white">{(comparison.summary.total_invoiced / 1000).toFixed(1)}k</p>
                <p className="text-sm text-white/40 mt-1">{comparison.summary.total_invoiced.toLocaleString("sv-SE")} kr</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-[#2a2a2a] to-[#222] border-white/[0.06] overflow-hidden relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#6dbf4a]" />
              <CardContent className="pt-6 pb-5">
                <p className="text-xs uppercase tracking-wider text-white/40 mb-2">Tid rapporterat</p>
                <p className="text-3xl font-bold text-[#6dbf4a]">{(comparison.summary.total_reported / 1000).toFixed(1)}k</p>
                <p className="text-sm text-white/40 mt-1">{comparison.summary.total_reported.toLocaleString("sv-SE")} kr</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-[#2a2a2a] to-[#222] border-white/[0.06] overflow-hidden relative">
              <div className={`absolute top-0 left-0 w-full h-1 ${comparison.summary.difference >= 0 ? "bg-emerald-500" : "bg-orange-500"}`} />
              <CardContent className="pt-6 pb-5">
                <p className="text-xs uppercase tracking-wider text-white/40 mb-2">Avvikelse</p>
                <p className={`text-3xl font-bold ${comparison.summary.difference >= 0 ? "text-emerald-400" : "text-orange-400"}`}>
                  {comparison.summary.difference >= 0 ? "+" : ""}{(comparison.summary.difference / 1000).toFixed(1)}k
                </p>
                <p className="text-sm text-white/40 mt-1">{comparison.summary.difference.toLocaleString("sv-SE")} kr</p>
              </CardContent>
            </Card>
          </div>

          {/* Per project table */}
          {comparison.comparisons.length > 0 ? (
            <Card className="bg-[#0d1218] border-white/[0.06]">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <ArrowUpDown className="w-5 h-5 text-[#6dbf4a]" /> Per projekt
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/[0.06]">
                      <TableHead className="text-white/40">Projekt</TableHead>
                      <TableHead className="text-white/40 text-right">Fakturor</TableHead>
                      <TableHead className="text-white/40 text-right">Fakturerat</TableHead>
                      <TableHead className="text-white/40 text-right">Rapporterat</TableHead>
                      <TableHead className="text-white/40 text-right">Avvikelse</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {comparison.comparisons.map(c => (
                      <TableRow key={c.project_id} className="border-white/[0.06]">
                        <TableCell>
                          <div>
                            <span className="text-white font-medium">{c.project_name}</span>
                            {c.work_number && <span className="text-[#6dbf4a] font-mono text-xs ml-2 bg-[#6dbf4a]/10 px-1.5 py-0.5 rounded">{c.work_number}</span>}
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-white/40">{c.invoice_count} st</TableCell>
                        <TableCell className="text-right text-white">{c.invoice_total.toLocaleString("sv-SE")} kr</TableCell>
                        <TableCell className="text-right text-[#6dbf4a]">{c.time_report_total.toLocaleString("sv-SE")} kr</TableCell>
                        <TableCell className="text-right">
                          <span className={`font-semibold flex items-center justify-end gap-1 ${c.difference >= 0 ? "text-emerald-400" : "text-orange-400"}`}>
                            {c.difference >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                            {c.difference >= 0 ? "+" : ""}{c.difference.toLocaleString("sv-SE")} kr
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <div className="text-center py-16">
              <ArrowUpDown className="w-10 h-10 mx-auto mb-3 text-gray-700" />
              <p className="text-white/40 text-sm">Inga matchade fakturor att jämföra</p>
              <p className="text-white/25 text-xs mt-1">Importera och matcha fakturor mot projekt först</p>
            </div>
          )}
        </div>
      )}

      {/* Invoice Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-[#0d1218] border-white/[0.06]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <FileText className="w-6 h-6 text-red-400" />
              Fakturadetaljer
            </DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-5">
              {/* File info */}
              <div className="flex items-center gap-3 p-3 bg-[#080c12] rounded-xl">
                <FileText className="w-8 h-8 text-red-400" />
                <div className="flex-1">
                  <p className="text-white font-medium">{selectedInvoice.filename}</p>
                  <p className="text-xs text-white/40">
                    {(selectedInvoice.file_size / 1024).toFixed(0)} KB
                    {" · "}{selectedInvoice.uploaded_by_name}
                    {" · "}{selectedInvoice.uploaded_at?.slice(0, 10)}
                  </p>
                </div>
                <Button size="sm" variant="outline" className="border-white/[0.06] text-white/40"
                  onClick={() => handleViewFile(selectedInvoice)} data-testid="view-invoice-file">
                  <Eye className="w-4 h-4 mr-1" /> Visa
                </Button>
              </div>

              {/* Extracted data */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#080c12] rounded-xl p-3">
                  <p className="text-xs text-white/40 mb-1 flex items-center gap-1"><Building2 className="w-3 h-3" /> Leverantör</p>
                  <p className="text-white text-sm">{selectedInvoice.supplier_name || "—"}</p>
                </div>
                <div className="bg-[#080c12] rounded-xl p-3">
                  <p className="text-xs text-white/40 mb-1 flex items-center gap-1"><Hash className="w-3 h-3" /> Fakturanummer</p>
                  <p className="text-white text-sm">{selectedInvoice.invoice_number || "—"}</p>
                </div>
                <div className="bg-[#080c12] rounded-xl p-3">
                  <p className="text-xs text-white/40 mb-1 flex items-center gap-1"><DollarSign className="w-3 h-3" /> Belopp</p>
                  <p className="text-[#6dbf4a] text-sm font-semibold">
                    {selectedInvoice.invoice_amount != null ? `${selectedInvoice.invoice_amount.toLocaleString("sv-SE")} kr` : "—"}
                  </p>
                </div>
                <div className="bg-[#080c12] rounded-xl p-3">
                  <p className="text-xs text-white/40 mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Fakturadatum</p>
                  <p className="text-white text-sm">{selectedInvoice.invoice_date || "—"}</p>
                </div>
              </div>

              {/* Match status */}
              <div className="p-3 bg-[#080c12] rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-white/40">Matchningsstatus</p>
                  {getStatusBadge(selectedInvoice.match_status)}
                </div>
                {selectedInvoice.project_name && (
                  <p className="text-sm text-white">Kopplad till: <span className="text-[#6dbf4a] font-medium">{selectedInvoice.project_name}</span></p>
                )}
              </div>

              {/* Manual match */}
              <div className="p-3 bg-[#080c12] rounded-xl space-y-3">
                <p className="text-sm text-white/40">Koppla till projekt</p>
                <div className="flex gap-2">
                  <Select value={matchProject} onValueChange={setMatchProject}>
                    <SelectTrigger className="bg-[#0d1218] border-white/[0.06] text-white flex-1" data-testid="match-project-select">
                      <SelectValue placeholder="Välj projekt..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0d1218] border-white/[0.06]">
                      {projects.map(p => (
                        <SelectItem key={p.id} value={p.id} className="text-white">
                          {p.name} {p.work_number && <span className="text-white/40 ml-1">({p.work_number})</span>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={() => matchProject && handleManualMatch(selectedInvoice.id, matchProject)}
                    disabled={!matchProject}
                    className="bg-[#6dbf4a] hover:bg-[#5da83d]" data-testid="match-btn">
                    <Link2 className="w-4 h-4 mr-1" /> Koppla
                  </Button>
                </div>
              </div>

              {/* Extracted text preview */}
              {selectedInvoice.extracted_text && (
                <details className="p-3 bg-[#080c12] rounded-xl">
                  <summary className="text-sm text-white/40 cursor-pointer hover:text-white/40">Extraherad text</summary>
                  <pre className="text-xs text-white/40 mt-2 whitespace-pre-wrap max-h-48 overflow-y-auto font-mono">
                    {selectedInvoice.extracted_text}
                  </pre>
                </details>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoiceImport;
